import { dateYYYYMMDD } from "$root/utils";

/**
 * The Ask Sphere welcome greeting: which one to show ({@link getGreetingKey}) and whether to
 * show it at all today ({@link shouldShowGreetingToday}).
 *
 * Both halves live here, next to their only caller, rather than in `$root/utils`: the keys
 * they return are Sphere AI's own i18n subtree (`sphereAi.greeting.*`), and a greeting helper
 * parked in the global utils barrel would split this feature's copy across two namespaces.
 *
 * ## Once-per-day gate
 *
 * The full personalized greeting shows only on the **first panel open of the current
 * local calendar day**; later opens the same day keep just the context line. We persist
 * the last day the greeting was shown as a local `YYYY-MM-DD` string in `localStorage`
 * (client-only flag — it does not need to follow the user across devices).
 *
 * Read and write are kept separate on purpose: {@link shouldShowGreetingToday} is a pure
 * check with no side effect, and {@link markGreetingShown} does the persist. Callers read
 * during render and mark in an effect, so a double-invoked render (React StrictMode) can't
 * consume the greeting before it is actually shown.
 */
const LAST_GREETING_DAY_KEY = "sphere-ai:last-greeting-day:v1";

/** The local calendar day (`YYYY-MM-DD`, browser timezone) for a moment. */
function localDay(now: Date): string {
	return dateYYYYMMDD(now);
}

/** `true` iff the greeting has not yet been shown on `now`'s local calendar day. */
export function shouldShowGreetingToday(now: Date): boolean {
	try {
		return localStorage.getItem(LAST_GREETING_DAY_KEY) !== localDay(now);
	} catch {
		// storage unavailable → fail open: showing the greeting is harmless.
		return true;
	}
}

/** Record that the greeting was shown on `now`'s local calendar day. */
export function markGreetingShown(now: Date): void {
	try {
		localStorage.setItem(LAST_GREETING_DAY_KEY, localDay(now));
	} catch {
		// storage unavailable / quota — degrades to showing the greeting again next open.
	}
}

/** Time-of-day greeting keys, resolved by the caller through i18n `t()` with the user's name. */
export type GreetingKey =
	| "sphereAi.greeting.morning"
	| "sphereAi.greeting.afternoon"
	| "sphereAi.greeting.evening"
	| "sphereAi.greeting.neutral";

/**
 * Picks the greeting key for a moment using the **browser's local wall-clock hour**
 * (`Date#getHours`). Bands:
 *
 * - `05:00–11:59` → morning
 * - `12:00–17:59` → afternoon
 * - `18:00–21:59` → evening
 * - `22:00–04:59` → neutral
 *
 * Local time only. Do **not** reuse this for market-hours / server-time content — that
 * is a different temporal source and must stay separate.
 */
export function getGreetingKey(date: Date): GreetingKey {
	const hour = date.getHours();
	if (hour >= 5 && hour < 12) {
		return "sphereAi.greeting.morning";
	}
	if (hour >= 12 && hour < 18) {
		return "sphereAi.greeting.afternoon";
	}
	if (hour >= 18 && hour < 22) {
		return "sphereAi.greeting.evening";
	}
	return "sphereAi.greeting.neutral";
}
